import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type WindowService from '@/services/browser/windowService'
import type CookieConsentService from '@/services/browser/cookieConsentService'
import type { TikTokClickIds } from '@/services/tracking/types'
import { deleteCookieByName, getCookieByName, setCookieByName } from '@/utils/cookies'
import type { Nullable } from '@/types'

const TTCLID_URL_PARAM = 'ttclid'

const TTCLID_COOKIE_NAME = 'tm_tiktok_ttclid'

const PIXEL_TTCLID_COOKIE_NAME = 'ttclid'

const TTCLID_COOKIE_LIFETIME_DAYS = 30
const ROOT_DOMAIN = 'textmagic.com'
const SHARED_COOKIE_DOMAIN = `.${ROOT_DOMAIN}`

@injectable()
export default class TikTokAttributionService {
  constructor(
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
    @inject(SERVICE_TYPES.CookieConsentService) protected readonly cookieConsentService: CookieConsentService,
  ) {}

  public syncTtclidFromUrl(): void {
    if (!this.cookieConsentService.hasAdvertisementConsent()) {
      this.removeTtclidCookie()
      return
    }

    const ttclid = this.getTtclidFromUrl()

    if (!ttclid) {
      return
    }

    setCookieByName(TTCLID_COOKIE_NAME, ttclid, TTCLID_COOKIE_LIFETIME_DAYS, this.getSharedCookieDomain())
  }

  public getClickIds(): TikTokClickIds {
    if (!this.cookieConsentService.hasAdvertisementConsent()) {
      return { ttclid: null }
    }

    const ttclid = this.getTtclidFromUrl() ?? getCookieByName(TTCLID_COOKIE_NAME)

    return {
      ttclid: ttclid ? this.safeDecode(ttclid) : getCookieByName(PIXEL_TTCLID_COOKIE_NAME),
    }
  }

  public removeTtclidCookie(): void {
    deleteCookieByName(TTCLID_COOKIE_NAME, this.getSharedCookieDomain())
  }

  protected getTtclidFromUrl(): Nullable<string> {
    const prefix = `${TTCLID_URL_PARAM}=`
    const pair = this.windowService
      .self()
      .location.search.replace(/^\?/, '')
      .split('&')
      .find((item) => item.startsWith(prefix))

    return pair?.slice(prefix.length) || null
  }

  protected safeDecode(value: string): string {
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  protected getSharedCookieDomain(): string | undefined {
    const { hostname } = this.windowService.self().location

    const isTextmagicHost = hostname === ROOT_DOMAIN || hostname.endsWith(SHARED_COOKIE_DOMAIN)

    return isTextmagicHost ? SHARED_COOKIE_DOMAIN : undefined
  }
}
